import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Creator } from "@/entities/Creator";
import { Product } from "@/entities/Product";
import { Project } from "@/entities/Project";
import { Offering } from "@/entities/Offering";
import {
  Star, MapPin, Instagram, Twitter, Youtube, ExternalLink,
  Package, Briefcase, Sparkles, ArrowLeft, ShoppingBag,
  Clock, CheckCircle, Zap, TrendingUp, Users, Award
} from "lucide-react";

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    let videoId = null;
    if (u.hostname.includes("youtu.be")) videoId = u.pathname.slice(1);
    else if (u.hostname.includes("youtube.com")) videoId = u.searchParams.get("v");
    if (!videoId) return null;
    return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=0`;
  } catch { return null; }
}

// Compute a reputation score 0–100 from database metrics
function computeScore(creator, products, projects, offerings) {
  const salesScore = Math.min((creator.total_sales || 0) / 2, 30); // max 30pts
  const projectScore = Math.min(projects.filter(p => p.status === "completed" || p.status === "featured").length * 5, 25); // max 25pts
  const productScore = Math.min(products.length * 2, 20); // max 20pts
  const offeringScore = Math.min(offerings.length * 3, 15); // max 15pts
  const profileScore = creator.youtube_video_url ? 5 : 0; // 5pts for video
  const bioScore = creator.bio?.length > 100 ? 5 : 2; // up to 5pts
  return Math.round(Math.min(salesScore + projectScore + productScore + offeringScore + profileScore + bioScore, 100));
}

function ScoreBadge({ score }) {
  const tier = score >= 80 ? { label: "Elite", color: "bg-amber-500", text: "text-white" }
    : score >= 60 ? { label: "Pro", color: "bg-purple-500", text: "text-white" }
    : score >= 40 ? { label: "Rising", color: "bg-blue-500", text: "text-white" }
    : { label: "New", color: "bg-gray-400", text: "text-white" };
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-14 h-14">
        <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
          <circle cx="28" cy="28" r="24" fill="none" stroke="#e8e4de" strokeWidth="4" />
          <circle cx="28" cy="28" r="24" fill="none" stroke="#c8a96e" strokeWidth="4"
            strokeDasharray={`${(score / 100) * 150.8} 150.8`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-[#1a1a1a]">{score}</span>
      </div>
      <div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tier.color} ${tier.text}`}>{tier.label}</span>
        <p className="text-[10px] text-gray-400 mt-0.5">Creator Score</p>
      </div>
    </div>
  );
}

function ProjectCard({ project }) {
  return (
    <div className="bg-white border border-[#e8e4de] rounded-2xl overflow-hidden hover:shadow-lg transition-all group">
      {project.images?.[0] && (
        <div className="h-44 overflow-hidden">
          <img src={project.images[0]} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
            project.status === "featured" ? "bg-[#c8a96e] text-white" :
            project.status === "completed" ? "bg-green-100 text-green-700" :
            "bg-blue-100 text-blue-700"
          }`}>
            {project.status === "featured" ? "★ Featured" : project.status === "completed" ? "Completed" : "In Progress"}
          </span>
          {project.category && <span className="text-[10px] text-gray-400">{project.category}</span>}
        </div>
        <h3 className="font-semibold text-[#1a1a1a] mb-1 group-hover:text-[#c8a96e] transition-colors">{project.title}</h3>
        <p className="text-xs text-gray-500 line-clamp-3 leading-relaxed mb-3">{project.description}</p>
        {project.client_name && <p className="text-xs text-gray-400">Client: {project.client_name}</p>}
        {project.project_url && (
          <a href={project.project_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#c8a96e] font-medium mt-2 hover:underline">
            View Project <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
}

function ProductCard({ product }) {
  return (
    <Link to={createPageUrl(`ProductDetail?id=${product.id}`)} className="group bg-white border border-[#e8e4de] rounded-2xl overflow-hidden hover:shadow-lg transition-all">
      <div className="h-44 overflow-hidden bg-[#f5f3ef]">
        <img src={product.images?.[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-4">
        <p className="font-semibold text-[#1a1a1a] text-sm mb-1 group-hover:text-[#c8a96e] transition-colors line-clamp-1">{product.name}</p>
        <p className="text-xs text-gray-400 mb-2 line-clamp-2">{product.description}</p>
        <div className="flex items-center justify-between">
          <span className="font-bold text-[#1a1a1a]">${product.price}</span>
          {product.rating && (
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Star className="w-3 h-3 fill-[#c8a96e] text-[#c8a96e]" />{product.rating}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function OfferingCard({ offering }) {
  const priceLabel = offering.price_type === "free" ? "Free"
    : offering.price_type === "contact" ? "Contact for Price"
    : offering.price_type === "starting_at" ? `From $${offering.price}`
    : `$${offering.price}`;

  return (
    <div className="bg-white border border-[#e8e4de] rounded-2xl overflow-hidden hover:shadow-lg transition-all group">
      {offering.images?.[0] && (
        <div className="h-40 overflow-hidden">
          <img src={offering.images[0]} alt={offering.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold bg-[#faf9f7] border border-[#e8e4de] text-[#c8a96e] px-2 py-0.5 rounded-full">{offering.type}</span>
          {offering.is_featured && <span className="text-[10px] font-bold text-[#c8a96e]">★ Featured</span>}
        </div>
        <h3 className="font-semibold text-[#1a1a1a] mb-1 group-hover:text-[#c8a96e] transition-colors">{offering.title}</h3>
        <p className="text-xs text-gray-500 line-clamp-3 leading-relaxed mb-3">{offering.description}</p>
        {offering.duration && (
          <p className="text-xs text-gray-400 flex items-center gap-1 mb-2"><Clock className="w-3 h-3" />{offering.duration}</p>
        )}
        {offering.deliverables?.length > 0 && (
          <ul className="mb-3 space-y-1">
            {offering.deliverables.slice(0, 3).map((d, i) => (
              <li key={i} className="text-xs text-gray-500 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />{d}</li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between pt-3 border-t border-[#e8e4de]">
          <span className="font-bold text-[#1a1a1a]">{priceLabel}</span>
          <button className="text-xs bg-[#1a1a1a] hover:bg-[#c8a96e] text-white px-4 py-1.5 rounded-full transition-colors font-medium">
            Get This
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CreatorProfile() {
  const [creator, setCreator] = useState(null);
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("projects");
  const [score, setScore] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    if (!id) { navigate(createPageUrl("Creators")); return; }

    async function load() {
      const [c, prods, projs, offs] = await Promise.all([
        Creator.filter({ id }, "-createdAt", 1).then(r => r[0]),
        Product.filter({ creator_id: id, status: "active" }, "-createdAt", 20),
        Project.filter({ creator_id: id }, "-createdAt", 20),
        Offering.filter({ creator_id: id, status: "active" }, "-createdAt", 20),
      ]);
      setCreator(c);
      setProducts(prods);
      setProjects(projs);
      setOfferings(offs);
      setScore(computeScore(c, prods, projs, offs));
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin w-8 h-8 border-4 border-[#c8a96e] border-t-transparent rounded-full" />
    </div>
  );

  if (!creator) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <p className="text-gray-500 mb-4">Creator not found.</p>
      <Link to={createPageUrl("Creators")} className="text-[#c8a96e] hover:underline text-sm">← Back to Creators</Link>
    </div>
  );

  const embedUrl = getYouTubeEmbedUrl(creator.youtube_video_url);
  const completedProjects = projects.filter(p => p.status === "completed" || p.status === "featured").length;

  const tabs = [
    { id: "projects", label: "Projects", icon: <Briefcase className="w-4 h-4" />, count: projects.length },
    { id: "products", label: "Products", icon: <Package className="w-4 h-4" />, count: products.length },
    { id: "offerings", label: "Offerings", icon: <Sparkles className="w-4 h-4" />, count: offerings.length },
  ];

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      {/* Video / Banner Hero */}
      <div className="relative bg-[#1a1a1a]">
        {embedUrl ? (
          <div className="relative w-full" style={{ paddingTop: "42%" }}>
            <iframe
              src={embedUrl}
              title={`${creator.name} intro`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        ) : (
          <div className="relative h-64 md:h-80 overflow-hidden">
            <img
              src={creator.banner_image || "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1400&h=500&fit=crop"}
              alt={creator.name}
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <Youtube className="w-12 h-12 mx-auto mb-2 opacity-40" />
                <p className="text-sm opacity-60">Video intro coming soon</p>
              </div>
            </div>
          </div>
        )}
        {/* Back button */}
        <Link to={createPageUrl("Creators")} className="absolute top-4 left-4 z-10 flex items-center gap-1.5 bg-black/50 hover:bg-black/70 text-white text-xs font-medium px-3 py-1.5 rounded-full transition-colors backdrop-blur-sm">
          <ArrowLeft className="w-3.5 h-3.5" /> All Creators
        </Link>
      </div>

      {/* Profile Header */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-[#e8e4de] rounded-2xl -mt-8 relative z-10 p-6 mb-6 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            {/* Avatar */}
            <img
              src={creator.avatar || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop"}
              alt={creator.name}
              className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow-md flex-shrink-0"
            />
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div>
                  <h1 className="font-display text-2xl md:text-3xl font-bold text-[#1a1a1a]">{creator.name}</h1>
                  <p className="text-[#c8a96e] font-medium text-sm">{creator.primary_category || creator.specialty}</p>
                </div>
                <ScoreBadge score={score} />
              </div>
              {creator.location && (
                <p className="text-xs text-gray-400 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" />{creator.location}</p>
              )}
              <p className="text-sm text-gray-600 leading-relaxed mb-4">{creator.bio}</p>
              {/* Social + Stats row */}
              <div className="flex flex-wrap items-center gap-4">
                {creator.social_instagram && (
                  <a href={`https://instagram.com/${creator.social_instagram.replace("@","")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#c8a96e] transition-colors">
                    <Instagram className="w-3.5 h-3.5" />{creator.social_instagram}
                  </a>
                )}
                {creator.social_twitter && (
                  <a href={`https://twitter.com/${creator.social_twitter.replace("@","")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#c8a96e] transition-colors">
                    <Twitter className="w-3.5 h-3.5" />{creator.social_twitter}
                  </a>
                )}
                {creator.youtube_video_url && (
                  <a href={creator.youtube_video_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-red-500 hover:underline">
                    <Youtube className="w-3.5 h-3.5" /> YouTube
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Metric Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-[#e8e4de]">
            {[
              { icon: <TrendingUp className="w-4 h-4 text-[#c8a96e]" />, value: creator.total_sales || 0, label: "Total Sales" },
              { icon: <CheckCircle className="w-4 h-4 text-green-500" />, value: completedProjects, label: "Projects Done" },
              { icon: <Package className="w-4 h-4 text-blue-500" />, value: products.length, label: "Products" },
              { icon: <Sparkles className="w-4 h-4 text-purple-500" />, value: offerings.length, label: "Offerings" },
            ].map((s, i) => (
              <div key={i} className="bg-[#faf9f7] rounded-xl p-3 text-center">
                <div className="flex justify-center mb-1">{s.icon}</div>
                <div className="font-display text-xl font-bold text-[#1a1a1a]">{s.value}</div>
                <div className="text-[10px] text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-[#e8e4de] rounded-xl p-1 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.id
                  ? "bg-[#1a1a1a] text-white shadow-sm"
                  : "text-gray-500 hover:text-[#1a1a1a]"
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === tab.id ? "bg-white/20 text-white" : "bg-[#f5f3ef] text-gray-400"}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="pb-16">
          {/* Projects Tab */}
          {activeTab === "projects" && (
            <div>
              {projects.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No projects posted yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {projects.map(p => <ProjectCard key={p.id} project={p} />)}
                </div>
              )}
            </div>
          )}

          {/* Products Tab */}
          {activeTab === "products" && (
            <div>
              {products.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No products listed yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {products.map(p => <ProductCard key={p.id} product={p} />)}
                </div>
              )}
            </div>
          )}

          {/* Offerings Tab */}
          {activeTab === "offerings" && (
            <div>
              {offerings.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No offerings posted yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {offerings.map(o => <OfferingCard key={o.id} offering={o} />)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}